import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

const NewAdminLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAdmin, checkSession } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Check if user is already authenticated and admin
  useEffect(() => {
    const checkAuth = async () => {
      await checkSession();
      if (isAuthenticated && isAdmin) {
        navigate('/new-admin');
      }
    };
    
    checkAuth();
  }, [isAuthenticated, isAdmin, navigate, checkSession]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('Attempting admin login for:', email);
      
      // Try to sign in with the provided credentials
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password: password.trim()
      });

      if (signInError) {
        console.error('Sign in error details:', signInError);
        throw new Error('メールアドレスまたはパスワードが正しくありません');
      }
      
      if (!authData.user) throw new Error('ログインに失敗しました');

      console.log('Login successful, checking admin role for:', authData.user.email);
      
      // Check if user has admin role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', authData.user.id)
        .eq('role', 'admin')
        .maybeSingle();

      console.log('Role check result:', roleData, roleError);

      if (roleError) {
        console.error('Role check error:', roleError);
        await supabase.auth.signOut();
        throw new Error('権限の確認中にエラーが発生しました。もう一度ログインしてください。');
      }

      if (!roleData) {
        console.warn('User does not have admin role:', authData.user.email);
        await supabase.auth.signOut();
        throw new Error('管理者権限がありません');
      }

      console.log('Admin authentication successful');
      await checkSession(); // Refresh the session
      navigate('/new-admin');
    } catch (err) {
      console.error('Admin login error:', err);
      let errorMessage = 'ログインに失敗しました';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Shield className="h-12 w-12 text-gray-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              新管理者ログイン
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              管理者アカウントでログインしてください
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                メールアドレス
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                placeholder="管理者メールアドレス"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                パスワード
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-gray-600 hover:text-gray-500"
              >
                通常のログインページへ戻る
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewAdminLoginPage;